import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '@selfai-platform/shared';
import { catchError, map } from 'rxjs/operators';
import { SchemaField } from '../types/service-interfaces';

export interface WorkflowNodeSchema {
  nodeId: string;
  workflowId: string;
  schema: SchemaField[];
  timestamp: Date;
}

export interface SchemaExtractionResult {
  success: boolean;
  schema?: SchemaField[];
  error?: string;
  nodeId?: string;
  workflowId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SchemaExtractionService {
  private readonly baseUrl = '/v1/reports';
  private schemaCache = new Map<string, WorkflowNodeSchema>();

  constructor(
    private http: HttpClient,
    private translateService: TranslateService,
    private readonly alertService: AlertService
  ) {}

  public extractSchemaFromNode(workflowId: string, nodeId: string): Observable<SchemaExtractionResult> {
    const cacheKey = `${workflowId}:${nodeId}`;

    const cached = this.schemaCache.get(cacheKey);
    if (cached && this.isCacheValid(cached)) {
      return of({
        success: true,
        schema: cached.schema,
        nodeId,
        workflowId,
      });
    }

    const url = `${this.baseUrl}/${workflowId}/node/${nodeId}`;

    return this.http.get<any>(url).pipe(
      map((response) => {
        const schema = this.parseSchemaFromResponse(response);

        this.schemaCache.set(cacheKey, {
          nodeId,
          workflowId,
          schema,
          timestamp: new Date(),
        });

        return {
          success: true,
          schema,
          nodeId,
          workflowId,
        };
      }),
      catchError((error) => {
        this.alertService.error(this.translateService.instant('chat.errors.failed-to-extract-schema') + ` ${nodeId}: ` + (error.message || error));
        return of({
          success: false,
          error: error.message || this.translateService.instant('chat.errors.failed-extract-schema'),
          nodeId,
          workflowId,
        });
      })
    );
  }

  public extractSchemaFromContext(contextData: any): SchemaField[] | null {
    try {
      const possibleSchemas = [
        contextData?.schema,
        contextData?.operation?.schema,
        contextData?.nodeData?.schema,
        contextData?.metadata?.schema,
        contextData?.ports?.output?.[0]?.schema,
      ];

      for (const schema of possibleSchemas) {
        if (schema && Array.isArray(schema)) {
          return this.normalizeSchema(schema);
        }
      }

      return this.inferSchemaFromContext(contextData);
    } catch (error) {
      this.alertService.error(this.translateService.instant('chat.errors.error-extracting-schema-from-context') + ': ' + ((error as any)?.message || error));
      return null;
    }
  }

  public validateNodeContext(contextData: any): { isValid: boolean; missingFields: string[]; warnings: string[] } {
    const missingFields: string[] = [];
    const warnings: string[] = [];

    const workflowId = contextData?.workflowId || contextData?.workflow?.id;
    if (!workflowId) {
      missingFields.push('workflowId');
    }

    const nodeId = contextData?.id || contextData?.nodeId || contextData?.operation?.id;
    if (!nodeId) {
      missingFields.push('nodeId');
    }

    const schema = this.extractSchemaFromContext(contextData);
    if (!schema || schema.length === 0) {
      warnings.push(this.translateService.instant('chat.warnings.no-schema-execute-first'));
    }

    const nodeName = contextData?.operation?.name || contextData?.name;
    if (!nodeName) {
      warnings.push(this.translateService.instant('chat.warnings.node-name-not-found'));
    }

    return {
      isValid: missingFields.length === 0,
      missingFields,
      warnings,
    };
  }

  public extractInputNodeIds(contextData: any): string[] {
    return contextData?.inputNodeIds || contextData?.serializedData?.inputNodeIds || [];
  }

  public createEnhancedContext(contextData: any): Observable<any> {
    const validation = this.validateNodeContext(contextData);

    if (!validation.isValid) {
      return throwError(() => new Error(`Invalid context: missing ${validation.missingFields.join(', ')}`));
    }

    const workflowId = contextData?.workflowId || contextData?.workflow?.id;
    const originalNodeId = contextData?.id || contextData?.nodeId || contextData?.operation?.id;
    const inputNodeIds = this.extractInputNodeIds(contextData);

    const targetNodeId = inputNodeIds.length === 1 ? inputNodeIds[0] : originalNodeId;

    let schema = this.extractSchemaFromContext(contextData);

    const createEnhancedResult = (fetchedSchema: SchemaField[] | null, schemaSource: string, additionalWarnings: string[] = []) => {

      return {
        ...contextData,
        workflowId,
        nodeId: targetNodeId,
        originalNodeId,
        inputNodeIds,
        schema: fetchedSchema || [],
        schemaSource,
        validationWarnings: [...validation.warnings, ...additionalWarnings],
      };
    };

    if (!schema || schema.length === 0) {
      return this.extractSchemaFromNode(workflowId, targetNodeId).pipe(
        map((result) => {
          return createEnhancedResult(
            result.schema || [],
            result.success ? 'backend' : 'context'
          );
        }),
        catchError((error) => {
          this.alertService.error(this.translateService.instant('chat.errors.backend-schema-fetch-failed') + ': ' + (error.message || error));
          return of(createEnhancedResult(
            [],
            'none',
            [this.translateService.instant('chat.warnings.failed-backend-schema')]
          ));
        })
      );
    }

    if (inputNodeIds.length === 1) {
      return this.extractSchemaFromNode(workflowId, targetNodeId).pipe(
        map((result) => {
          return createEnhancedResult(
            result.schema || schema,
            result.success ? 'backend' : 'context'
          );
        }),
        catchError((error) => {
          this.alertService.error(this.translateService.instant('chat.errors.input-node-schema-fetch-failed') + ': ' + (error.message || error));
          return of(createEnhancedResult(
            schema,
            'context',
            [this.translateService.instant('chat.warnings.failed-input-schema')]
          ));
        })
      );
    }

    return of(createEnhancedResult(schema, 'context'));
  }


  private parseSchemaFromResponse(response: any): SchemaField[] {
    try {
      if (response?.resultEntities) {
        const firstKey = Object.keys(response.resultEntities)[0];
        if (firstKey && response.resultEntities[firstKey]?.report?.tables) {
          const tables = response.resultEntities[firstKey].report.tables;
          const columnNamesTable = tables.find((table: any) => table.name === 'Column Names and Types');

          if (columnNamesTable && columnNamesTable.values && columnNamesTable.values.length > 0) {
            const schema: SchemaField[] = [];

            for (let i = 0; i < columnNamesTable.values.length; i++) {
              const row = columnNamesTable.values[i];
              if (row && row.length >= 3) {
                const columnName = row[1];
                const columnType = row[2];

                schema.push({
                  metadata: {},
                  name: columnName,
                  nullable: true,
                  type: columnType
                });
              }
            }

            return schema;
          }
        }
      }

      if (response?.schema && Array.isArray(response.schema)) {
        return this.normalizeSchema(response.schema);
      }

      if (response?.data?.schema && Array.isArray(response.data.schema)) {
        return this.normalizeSchema(response.data.schema);
      }

      if (response?.result?.schema && Array.isArray(response.result.schema)) {
        return this.normalizeSchema(response.result.schema);
      }

      if (response?.columns && Array.isArray(response.columns)) {
        return response.columns.map((col: any) => ({
          name: col.name || col.column_name || '',
          type: col.type || col.data_type || 'string',
          nullable: col.nullable !== false,
          metadata: col.metadata || {},
        }));
      }

      return [];
    } catch (error) {
      this.alertService.error(this.translateService.instant('chat.errors.error-parsing-schema-from-response') + ': ' + ((error as any)?.message || error));
      return [];
    }
  }

  private normalizeSchema(schema: any[]): SchemaField[] {
    return schema.map((field) => ({
      name: field.name || field.column_name || '',
      type: field.type || field.data_type || 'string',
      nullable: field.nullable !== false,
      metadata: field.metadata || field.description ? { description: field.description } : {},
    }));
  }

  private inferSchemaFromContext(contextData: any): SchemaField[] | null {
    if (contextData?.sampleData && Array.isArray(contextData.sampleData) && contextData.sampleData.length > 0) {
      const firstRow = contextData.sampleData[0];
      return Object.keys(firstRow).map((key) => ({
        name: key,
        type: this.inferTypeFromValue(firstRow[key]),
        nullable: true,
        metadata: {},
      }));
    }

    if (contextData?.columns && Array.isArray(contextData.columns)) {
      return contextData.columns.map((col: any) => ({
        name: typeof col === 'string' ? col : col.name || '',
        type: typeof col === 'object' ? col.type || 'string' : 'string',
        nullable: true,
        metadata: {},
      }));
    }

    return null;
  }

  private inferTypeFromValue(value: any): string {
    if (value === null || value === undefined) return 'string';

    const type = typeof value;
    switch (type) {
      case 'number':
        return Number.isInteger(value) ? 'integer' : 'double';
      case 'boolean':
        return 'boolean';
      case 'string':
        if (!isNaN(Date.parse(value))) {
          return 'date';
        }
        return 'string';
      default:
        return 'string';
    }
  }

  private isCacheValid(cached: WorkflowNodeSchema): boolean {
    const fiveMinutes = 5 * 60 * 1000;
    return Date.now() - cached.timestamp.getTime() < fiveMinutes;
  }
}
